import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check, Activity, Sparkles, Heart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const Recuperacion = () => {
  const lang = useLanguage();
  const t = getPageTexts('recuperacion', lang);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const objectives = [
    { icon: Activity, title: t.obj1Title, desc: t.obj1Desc },
    { icon: Heart, title: t.obj2Title, desc: t.obj2Desc },
    { icon: Sparkles, title: t.obj3Title, desc: t.obj3Desc },
    { icon: Check, title: t.obj4Title, desc: t.obj4Desc },
  ];

  const phases = [
    { num: '01', title: t.p1Title, sub: t.p1Sub, items: t.p1Items || [], note: t.p1Note },
    { num: '02', title: t.p2Title, sub: t.p2Sub, items: t.p2Items || [], note: t.p2Note },
    { num: '03', title: t.p3Title, sub: t.p3Sub, items: t.p3Items || [], note: t.p3Note },
  ];

  const categories = [
    { title: t.cat1Title, items: t.cat1Items || [], desc: t.cat1Desc },
    { title: t.cat2Title, items: t.cat2Items || [], desc: t.cat2Desc },
    { title: t.cat3Title, items: t.cat3Items || [], desc: t.cat3Desc },
    { title: t.cat4Title, items: t.cat4Items || [], desc: t.cat4Desc },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.badge}</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1C1C1A] mb-6 leading-tight">
                {t.title1}<br /><span className="italic text-[#B8975A]">{t.title2}</span>
              </h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-base md:text-lg text-[#4A4845] leading-relaxed mb-4">{t.heroDesc1}</p>
              <p className="text-sm text-[#4A4845] leading-relaxed mb-8">{t.heroDesc2}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.bookEval} <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} /> {t.callNow}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="/images/recovery-closeup.jpg" alt="Recovery Center" className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Concept */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.conceptLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">{t.conceptTitle1}<br /><span className="italic text-[#B8975A]">{t.conceptTitle2}</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {objectives.map((item, i) => (
              <div key={i} className="p-6 border border-[#B8975A]/30 hover:border-[#B8975A] transition-colors text-center">
                <item.icon size={32} className="text-[#B8975A] mx-auto mb-4" />
                <h3 className="font-serif text-lg text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Phases */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.methodLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.phasesTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {phases.map((phase, i) => (
              <div key={i} className="bg-white p-8 border-t-2 border-[#B8975A]">
                <span className="text-[#B8975A] font-serif text-3xl">{phase.num}</span>
                <h3 className="font-serif text-xl text-[#1C1C1A] mt-4 mb-2">{phase.title}</h3>
                <p className="text-sm text-[#B8975A] mb-4">{phase.sub}</p>
                <ul className="space-y-2 text-[#4A4845] text-sm">
                  {phase.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check size={14} className="text-[#B8975A] mt-0.5 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#8A8780] mt-4 pt-3 border-t border-[#e0ddd7]">{phase.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protocols by surgery */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.customLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.adaptedTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, i) => (
              <div key={i} className="bg-[#F0EDE7] p-6">
                <h3 className="font-serif text-lg text-[#1C1C1A] mb-4 pb-3 border-b border-[#B8975A]/30">{cat.title}</h3>
                <ul className="space-y-2 mb-4">
                  {cat.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-[#4A4845]">
                      <span className="w-1.5 h-1.5 bg-[#B8975A] rounded-full flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#8A8780] italic">{cat.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#8A8780] mt-8">{t.eachProtocol}</p>
        </div>
      </section>

      {/* Technology */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.techLabel}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">{t.techTitle}</h2>
              <div className="space-y-6">
                {[
                  { title: t.tech1Title, desc: t.tech1Desc },
                  { title: t.tech2Title, desc: t.tech2Desc },
                  { title: t.tech3Title, desc: t.tech3Desc },
                ].map((tech, i) => (
                  <div key={i} className="border-l-2 border-[#B8975A] pl-6">
                    <h3 className="font-serif text-xl text-white mb-2">{tech.title}</h3>
                    <p className="text-white/60 text-sm">{tech.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img src="/images/tech-closeup.jpg" alt={t.techLabel} className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A] mb-6">{t.ctaTitle}</h2>
          <p className="text-[#4A4845] mb-10">{t.ctaDesc}</p>
          <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
            {t.ctaBtn} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Recuperacion;
